import React, { useState, useEffect } from 'react';
import { Search, Plus, Tag, Key, CheckCircle, ChevronDown, X } from 'lucide-react';
import { adminApi } from "../../repositories/admin";
import InviteUserModal from '../../components/modal/InviteUserModal';
import PageHeader from '../../components/ui/PageHeader';
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs';
import StatCard from '../../components/ui/StatCard';
import DataTable, { DataTableColumn } from '../../components/ui/DataTable';
import Pagination from '../../components/common/Pagination';
import { badgeBaseClass, badgeTones } from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import { cn } from '../../DashboardPage/lib/utils';

interface Label {
  name: string;
}

interface InviteCode {
  id: string;
  code: string;
  status: string;
  isUsed: boolean;
  usedAt: string | null;
  createdAt: string;
  expiryDate: string;
  Label: Label | null;
}

interface Pagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

export default function InviteUserPage() {
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [totalUsedCodes, setTotalUsedCodes] = useState(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState('all');
 const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getInviteCodes({
        page: pagination.page,
        limit: pagination.limit,
        search,
        searchType
      });
      setCodes(response.data.data); // Your backend returns { success, data, pagination }
      setPagination(response.data.pagination);
      setTotalUsedCodes(response.data.usedCodesCount);
    } catch (error) {
      console.error('Error fetching codes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search to prevent excessive API calls
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
      } else {
        fetchCodes();
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [search, searchType]);

  // Fetch data when page changes
  useEffect(() => {
    fetchCodes();
  }, [pagination.page]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };


  const totalCodes = pagination.totalItems;

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
   const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCodeGeneratedRefresh = () => {
    fetchCodes();
  }

  const columns: DataTableColumn<InviteCode>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (code) => (
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 shrink-0 text-neutral-400" />
          <span className="font-medium">{code.code}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      // The tone follows isUsed. It previously did not: the pill was green
      // whatever the value, so an unused code rendered a green "Unused".
      cell: (code) => (
        <span className={cn(badgeBaseClass, code.isUsed ? badgeTones.success : badgeTones.neutral)}>
          {code.isUsed ? 'Used' : 'Unused'}
        </span>
      ),
    },
    {
      key: 'label',
      header: 'Label',
      cell: (code) =>
        code.Label ? (
          // Neutral category tag — the same treatment label names already get on
          // /dsp-organizations. See the note in the summary.
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium border rounded-full text-neutral-700 bg-neutral-100 border-neutral-200">
            <Tag size={12} className="shrink-0" />
            {code.Label.name}
          </span>
        ) : (
          <span className="text-neutral-400">No label</span>
        ),
    },
    {
      key: 'created',
      header: 'Created',
      cell: (code) => <span className="text-neutral-600">{formatDate(code.createdAt)}</span>,
    },
    {
      key: 'expires',
      header: 'Expires',
      cell: (code) =>
        new Date(code.expiryDate) < new Date() ? (
          <span className="font-medium text-danger-600">Expired</span>
        ) : (
          <span className="text-neutral-600">{formatDate(code.expiryDate)}</span>
        ),
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Trial Code" }]}
        className="mb-6"
        title="Trial Code"
        description="Manage your free trial codes."
        actions={
          <Button onClick={handleOpenModal} className="w-full sm:w-auto">
            <Plus size={16} />
            Invite
          </Button>
        }
      />

      {/*
        Two cards, so the row is sized for two rather than leaving a third of a
        three-column grid empty. StatCard's badge is always circular, which
        settles the shape mismatch between these two structurally.
      */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:max-w-2xl">
        <StatCard
          label="Used Codes"
          value={totalUsedCodes ?? 0}
          icon={<CheckCircle className="w-5 h-5" />}
          iconClassName="bg-success-50 text-success-700"
        />
        <StatCard
          label="Total Codes"
          value={totalCodes}
          icon={<Key className="w-5 h-5" />}
        />
      </div>

      {/*
        Search type + term as one control: a single border, radius and
        focus-within treatment wrap both, with an inset divider between them.
        Same bar as Past Submissions / Master Roster / Contacts.
      */}
      <div className="flex items-stretch w-full mb-4 bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 lg:max-w-xl">
        <div className="relative shrink-0">
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            aria-label="Search trial codes by field"
            className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
          >
            {/*
              The initial searchType is "all" and the request already sends it,
              but there was no matching option — so the select rendered blank
              until the admin picked something.
            */}
            <option value="all">All Fields</option>
            <option value="code">Code</option>
            <option value="label">Label</option>
          </select>
          <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
        </div>

        <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

        <div className="relative flex-1 min-w-0">
          <Search
            size={16}
            className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400"
          />
          <input
            type="text"
            placeholder="Search codes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search trial codes"
            className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <h2 className="mb-3 text-lg font-semibold text-black">Trial Codes</h2>

      <DataTable
        columns={columns}
        rows={codes}
        getRowKey={(code) => code.id}
        loading={loading}
        skeletonRows={pagination.limit}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No codes found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Try adjusting your search, or invite a user to generate a code.
            </span>
          </span>
        }
      />

      {pagination.totalItems > 0 && (
        <div className="mt-4">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            setPage={handlePageChange}
            totalItems={pagination.totalItems}
            limit={pagination.limit}
          />
        </div>
      )}

      <InviteUserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCodeGenerated={handleCodeGeneratedRefresh}
      />
    </div>

  );
}
